/**
 * Automatismes — refonte UX, chantier 5 (maquette 7, validée le 24/09).
 *
 * Tout ce que VPC fait tout seul, écrit comme une phrase « Quand… alors… ».
 * Rien n'est inventé : les phrases sont construites à partir des règles RÉELLES
 * (GET /api/workflows/automatismes) — flèches de circuit qui notifient, préparent
 * un envoi ou rendent un budget ; délais d'étape ; échéances du catalogue.
 * Pour modifier : le circuit (éditeur visuel) ou Administration → Notifications.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Zap, Mail, Bell, ShieldCheck, Clock } from 'lucide-react';
import { Card, Badge } from '@/components/ui';
import { api, type AutomatismesDTO } from '@/lib/api';

const ROLES: Record<string, string> = { agent: 'un agent', manager: 'un manager', admin: 'un administrateur' };
const UNITE: Record<string, string> = { jours: 'jour(s)', mois: 'mois', annees: 'an(s)', semaines: 'semaine(s)' };
function delaisTexte(r: unknown): string {
  if (!Array.isArray(r) || r.length === 0) return '';
  const l = r.filter((x): x is { quantite: number; unite: string } => !!x && typeof x === 'object' && 'quantite' in x && 'unite' in x)
    .map((x) => `${x.quantite} ${UNITE[x.unite] ?? x.unite} avant`);
  return l.length ? ` (${l.join(', ')})` : '';
}

function Regle({ quand, alors, canaux, lien, lienTexte, actif = true }: {
  quand: React.ReactNode; alors: React.ReactNode; canaux: React.ReactNode; lien: string; lienTexte: string; actif?: boolean;
}) {
  return (
    <li className="flex items-start gap-4 border-b border-border/60 px-4 py-4">
      <span className="mt-0.5 grid h-9 w-9 flex-shrink-0 place-items-center rounded-r-lg bg-warning/15 text-warning"><Zap className="h-4 w-4" aria-hidden="true" /></span>
      <div className="min-w-0 flex-1 text-sm leading-relaxed text-ink">
        <span className="text-ink-muted">Quand</span> {quand}<br /><span className="text-ink-muted">alors</span> {alors}
        <div className="mt-2 flex flex-wrap gap-1.5">{canaux}{!actif && <Badge variant="neutral" size="sm">Circuit désactivé</Badge>}</div>
      </div>
      <Link to={lien} className="flex-shrink-0 text-sm text-accent hover:underline">{lienTexte}</Link>
    </li>
  );
}

export function AutomatismesPage() {
  const [d, setD] = useState<AutomatismesDTO | null>(null);
  const [filtre, setFiltre] = useState('');
  useEffect(() => { api.workflows.automatismes().then(setD).catch(() => setD({ fleches: [], delais: [], echeances: [] })); }, []);
  const circuits = useMemo(() => [...new Set((d?.fleches ?? []).map((f) => f.circuit).concat((d?.delais ?? []).map((x) => x.circuit)))].sort(), [d]);
  if (!d) return <p className="text-sm text-ink-muted">Chargement…</p>;
  const garde = (c: string) => !filtre || filtre === c;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1">
          <h1 className="font-display text-xl font-semibold text-ink">Automatismes</h1>
          <p className="mt-1 text-sm text-ink-muted">Tout ce que VPC fait tout seul, écrit comme une phrase : qui est prévenu, quand, et par quel moyen.</p>
        </div>
        <label className="text-sm"><span className="sr-only">Circuit</span>
          <select className="h-10 rounded-r-md border border-border bg-surface px-2" value={filtre} onChange={(e) => setFiltre(e.target.value)}>
            <option value="">Tous les circuits</option>{circuits.map((c) => <option key={c} value={c}>{c}</option>)}
          </select></label>
      </div>

      <Card variant="glass" className="overflow-hidden p-0">
        <h2 className="border-b border-border px-4 py-3 text-sm font-semibold text-ink">Dans les circuits</h2>
        <ul>
          {d.fleches.filter((f) => garde(f.circuit)).map((f) => (
            <Regle key={f.transitionId} actif={f.actif}
              quand={<>{ROLES[f.roleMinimum] ?? f.roleMinimum} choisit <b>« {f.libelle} »</b> (de « {f.de} » à « {f.vers} »)</>}
              alors={<>{[
                f.notification ? `${f.notification.cible ?? 'les personnes concernées'} ${f.notification.cible ? 'sont prévenus' : 'sont prévenues'}` : null,
                f.envoiConfig ? `un ${f.envoiConfig.canal === 'email' ? 'e-mail' : f.envoiConfig.canal === 'sms' ? 'SMS' : 'message interne'} est préparé${f.envoiConfig.validationRequise ? ', relu et validé avant envoi' : ''}` : null,
                f.recrediteBudget ? 'le montant réservé est rendu au budget' : null,
              ].filter(Boolean).join(' ; ')}</>}
              canaux={<>
                {f.notification && <Badge variant="info" size="sm"><Bell className="mr-1 inline h-3 w-3" aria-hidden="true" />Cloche</Badge>}
                {f.notification?.email && <Badge variant="neutral" size="sm"><Mail className="mr-1 inline h-3 w-3" aria-hidden="true" />E-mail</Badge>}
                {f.envoiConfig?.validationRequise && <Badge variant="warning" size="sm"><ShieldCheck className="mr-1 inline h-3 w-3" aria-hidden="true" />Relu avant envoi</Badge>}
                <Badge variant="neutral" size="sm">{f.circuit}</Badge>
              </>}
              lien="/admin/workflows" lienTexte="Voir le circuit" />
          ))}
          {d.delais.filter((x) => garde(x.circuit)).map((x) => (
            <Regle key={`${x.definitionId}-${x.etape}`} actif={x.actif}
              quand={<>un dossier reste à l’étape <b>« {x.etape} »</b></>}
              alors={<>il doit en sortir sous <b>{x.jours} jour(s)</b> (délai de l’étape)</>}
              canaux={<><Badge variant="neutral" size="sm"><Clock className="mr-1 inline h-3 w-3" aria-hidden="true" />Délai</Badge><Badge variant="neutral" size="sm">{x.circuit}</Badge></>}
              lien="/admin/workflows" lienTexte="Voir le circuit" />
          ))}
          {d.fleches.length + d.delais.length === 0 && <li className="px-4 py-4 text-sm text-ink-subtle">Aucune règle automatique dans vos circuits.</li>}
        </ul>
      </Card>

      {!filtre && (
        <Card variant="glass" className="overflow-hidden p-0">
          <h2 className="border-b border-border px-4 py-3 text-sm font-semibold text-ink">Les échéances suivies</h2>
          <ul>
            {d.echeances.map((e) => (
              <Regle key={e.code}
                quand={<>l’échéance <b>« {e.libelle} »</b> approche{delaisTexte(e.rappels)}</>}
                alors={<>{e.cible ?? 'les personnes concernées'} {e.cible ? 'sont prévenus' : 'sont prévenues'}</>}
                canaux={<>
                  <Badge variant="info" size="sm"><Bell className="mr-1 inline h-3 w-3" aria-hidden="true" />Cloche</Badge>
                  {e.email && <Badge variant="neutral" size="sm"><Mail className="mr-1 inline h-3 w-3" aria-hidden="true" />E-mail</Badge>}
                  {e.groupe && <Badge variant="neutral" size="sm">{e.groupe}</Badge>}
                </>}
                lien="/admin/echeances" lienTexte="Régler les délais" />
            ))}
          </ul>
        </Card>
      )}
      <p className="text-xs text-ink-subtle">Rien ne part jamais seul quand un envoi exige une relecture : il attend la validation de la personne désignée.</p>
    </div>
  );
}
